import { useEffect, useMemo, useState } from 'react';
import Spinner from '../../../components/common/Spinner';
import useSignupStore from '../../../store/useSignupStore';
import { showToast } from '../../../store/useToastStore';
import { IconChevron, IconCheck } from '../../../components/icons';

/* Schools have no icon of their own in the list, so each gets a stable leaf from
   this palette — same school, same mark, every render. */
const MARKS = ['🍀', '🌳', '🌱', '🌿', '🍃', '🌻'];
const markFor = (i) => MARKS[i % MARKS.length];

/* "Pre-K" → P, "Kinder" → K, "3rd grade" → 3 */
export const gradeMark = (name) => {
  const digits = String(name || '').match(/\d+/);
  return digits ? digits[0] : String(name || '?').trim().charAt(0).toUpperCase();
};

export default function SchoolsStep() {
  const { schools, schoolsLoading, joinGroups, zipCode } = useSignupStore();
  const loadSchools = useSignupStore((s) => s.loadSchools);
  const toggleGrade = useSignupStore((s) => s.toggleGrade);
  const goNext = useSignupStore((s) => s.goNext);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    loadSchools().catch((err) => showToast(err.message));
  }, [loadSchools]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter((s) => s.name.toLowerCase().includes(q));
  }, [schools, query]);

  return (
    <>
      <div className="ob-eyebrow">Join Group</div>
      <h1>Join your kids <em>school groups</em> 🏫</h1>
      <p className="sub ob-zip">Zip Code: {zipCode}</p>

      <div className="ob-search">
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
        <input type="text" value={query} placeholder="Search" onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="ob-scroll">
        {schoolsLoading && !schools.length ? (
          <div className="ob-loading"><Spinner variant="green" /> Finding schools near you…</div>
        ) : !visible.length ? (
          <div className="ob-grade-empty">
            {schools.length ? 'No school matches that search.' : 'No schools found near this ZIP code yet.'}
          </div>
        ) : (
          visible.map((school, i) => {
            const open = openId === school.id;
            const picked = school.grades.filter((g) => joinGroups.includes(g.networkId)).length;
            return (
              <div className={`ob-school ${open ? 'on' : ''}`} key={school.id}>
                <button type="button" className="ob-school-head" onClick={() => setOpenId(open ? null : school.id)}>
                  <span className="mk">
                    {school.photo ? <img src={school.photo} alt="" /> : markFor(i)}
                  </span>
                  <span className="info">
                    <span className="nm">{school.name}</span>
                    {school.distance != null && <span className="dist">{school.distance} miles away</span>}
                  </span>
                  {picked > 0 && <span className="ob-badge">{picked} grade{picked > 1 ? 's' : ''}</span>}
                  <IconChevron className={`ob-school-chev ${open ? 'on' : ''}`} />
                </button>
                {open && (
                  <div className="ob-grade-list">
                    {school.grades.length ? (
                      school.grades.map((g) => {
                        const on = joinGroups.includes(g.networkId);
                        return (
                          <button
                            type="button"
                            className={`ob-grade ${on ? 'on' : ''}`}
                            key={g.networkId}
                            onClick={() => toggleGrade(g.networkId)}
                          >
                            <span className="mk">{gradeMark(g.name)}</span>
                            <span className="nm">{g.name}</span>
                            <span className="ob-check">{on && <IconCheck />}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="ob-grade-empty">This school has no class groups yet.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <button className="btn btn-primary btn-primary-full ob-cta" disabled={!joinGroups.length} onClick={goNext}>
        {joinGroups.length ? 'Continue' : "Select your child's school + grade"}
      </button>
    </>
  );
}
